import { isPartnerWinery } from '@helpers/winery';
import { ALL_REVIEWS_ID, VINTAGE_LIST_ID, retry } from '@vivino/js-web-common';
import { RubyLibExploreVintagesPriceRange } from '@webtypes/rubyLibApi';
import React, { lazy } from 'react';
import { useUserContext } from 'vivino-js/context/UserContext';
import {
  AdPosition,
  Promotion,
  adsToBeShown,
  getExternalAdInfo,
} from 'vivino-js/helpers/adsHelper';
import { consoleLogVintage } from 'vivino-js/helpers/consoleHelpers';
import { isVCCountry } from 'vivino-js/helpers/countryHelpers';
import { HighlightTypes, getFilteredHighlights } from 'vivino-js/helpers/highlights';
import DynamicModuleLoader from 'vivino-ui/atoms/DynamicModuleLoader';
import PageSection from 'vivino-ui/atoms/PageSection';

import MeetTheRegion from '@components/MeetTheRegion/MeetTheRegion';
import MeetTheWinery from '@components/MeetTheWinery/MeetTheWinery';
import WineryCard, {
  WineryCardTrackingEvents,
  WineryCardTrackingSources,
} from '@components/WineryCard/WineryCard';

import ExternalAd from 'vivino-ui/components/ExternalAd';
import FoodPairing from 'vivino-ui/components/FoodPairing';
import { Grid } from 'vivino-ui/components/Grid';
import TasteCharacteristics from 'vivino-ui/components/TasteCharacteristics';
import VintageList from 'vivino-ui/components/VintageList';
import WineFacts from 'vivino-ui/components/WineFacts';
import WineTasteStyle from 'vivino-ui/components/WineTasteStyle';

import styles from './masterWinePage.scss';

const CommunityReviewsList = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "CommunityReviewsList" */
        'vivino-ui/components/CommunityReviews/components/CommunityReviewsList'
      )
  )
);

const OfferSubscription = lazy(() =>
  retry(
    () =>
      // @ts-ignore
      import(
        /* webpackChunkName: "OfferSubscription" */

        'vivino-js/components/OfferSubscription'
      )
  )
);

interface Props {
  countryCode: string;
  stateCode?: string;
  highlights: any[];
  vintage: any;
  wine: any;
  tastes: any;
  priceRange: RubyLibExploreVintagesPriceRange;
  pricesAndAvailabilities: any[];
  promotions: Promotion[];
}

const MasterWinePage = ({
  tastes,
  wine,
  vintage,
  stateCode,
  priceRange,
  highlights,
  countryCode,
  pricesAndAvailabilities,
  promotions,
}: Props) => {
  const position = AdPosition.MIDDLE;
  const externalAdInfo = getExternalAdInfo({
    promotions,
    position,
  });

  const dataAdUnit = externalAdInfo?.ad_unit_id;
  const adDivId = externalAdInfo?.div_id;

  const vintageWine = vintage?.wine;
  const winery = vintageWine?.winery;
  const allVintages = wine?.vintages || [];

  const filterdHighlights = getFilteredHighlights({
    highlights: highlights,
    filters: [HighlightTypes.PriceQpr],
  });

  const { user } = useUserContext();

  const showAds = adsToBeShown({
    promotions,
    user,
  });

  consoleLogVintage({ vintage });

  return (
    <div data-testid="masterWinePage">
      <Grid>
        {winery && (
          <PageSection>
            <MeetTheWinery vintageId={vintage.id} winery={winery} />
          </PageSection>
        )}

        {vintageWine?.region?.id && (
          <PageSection>
            <MeetTheRegion regionId={vintageWine?.region?.id} vintageId={vintageWine?.id} />
          </PageSection>
        )}

        <PageSection>
          <WineFacts vintage={vintage} />
        </PageSection>

        <DynamicModuleLoader id={ALL_REVIEWS_ID}>
          <PageSection>
            <CommunityReviewsList />
          </PageSection>
        </DynamicModuleLoader>

        <div id={VINTAGE_LIST_ID}>
          <PageSection>
            <VintageList
              vintages={allVintages}
              wine={vintage.wine}
              highlights={filterdHighlights}
            />
          </PageSection>
        </div>

        <PageSection>
          <TasteCharacteristics vintage={vintage} tastes={tastes} />
        </PageSection>

        <PageSection>
          <FoodPairing vintage={vintage} />
        </PageSection>

        {showAds && <ExternalAd divId={adDivId} dataAdSlot={dataAdUnit} />}
      </Grid>
      <PageSection>
        <WineTasteStyle styleData={vintageWine?.style} regionData={vintageWine?.region} />
      </PageSection>
      <Grid className={styles.layout}>
        {winery && !isPartnerWinery(winery) && (
          <PageSection>
            <WineryCard
              winery={winery}
              winesCTA={false}
              trackingSource={WineryCardTrackingSources.PDP}
              trackingEvents={{
                show: WineryCardTrackingEvents.PDPShow,
                learnMore: WineryCardTrackingEvents.PDPLearnMore,
              }}
            />
          </PageSection>
        )}
        {isVCCountry(countryCode) && (
          <DynamicModuleLoader>
            <PageSection>
              <OfferSubscription countryCode={countryCode} />
            </PageSection>
          </DynamicModuleLoader>
        )}
      </Grid>
    </div>
  );
};

export default MasterWinePage;
